import { Heading, Box, Text, Button } from 'theme-ui'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { useConversationData } from '../../util/conversation_data'
import PolisNet from '../../util/net'
import Spinner from '../framework/Spinner'
import strings from '../../strings/strings'

const InviteTree = () => {
  const params = useParams()
  const conversationData = useConversationData()
  const enabled = Boolean(conversationData?.treevite_enabled)
  const conversationId = useMemo(
    () => conversationData?.conversation_id || params.conversation_id,
    [conversationData?.conversation_id, params.conversation_id]
  )

  const [waves, setWaves] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [invitesPerUser, setInvitesPerUser] = useState('')
  const [ownerInvites, setOwnerInvites] = useState('')
  const [parentWaveOverride, setParentWaveOverride] = useState('') // empty string => default (latest)
  const [creating, setCreating] = useState(false)
  const [createdSummary, setCreatedSummary] = useState(null)

  const hasWaves = waves && waves.length > 0

  const loadWaves = async () => {
    if (!conversationId) return
    setLoading(true)
    setError(null)
    try {
      const res = await PolisNet.polisGet('/api/v3/treevite/waves', {
        conversation_id: conversationId
      })
      setWaves(Array.isArray(res) ? res : [])
    } catch (e) {
      setError(e?.responseText || e?.message || strings('dist_failed_load_waves'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (enabled) {
      loadWaves()
    }
  }, [enabled, conversationId])

  const canCreate = useMemo(() => {
    const ipu = Number(invitesPerUser || 0)
    const oi = Number(ownerInvites || 0)
    return ipu > 0 || oi > 0
  }, [invitesPerUser, ownerInvites])

  const handleCreateWave = async () => {
    if (!conversationId || !canCreate) return
    setCreating(true)
    setError(null)
    setCreatedSummary(null)
    try {
      const body = {
        conversation_id: conversationId
      }
      if (invitesPerUser !== '') body.invites_per_user = Number(invitesPerUser)
      if (ownerInvites !== '') body.owner_invites = Number(ownerInvites)
      if (parentWaveOverride !== '') body.parent_wave = Number(parentWaveOverride)

      const res = await PolisNet.polisPost('/api/v3/treevite/waves', body)
      setCreatedSummary({ wave: res?.wave, invites_created: res?.invites_created })
      // Refresh list and clear only the owner/ipu fields (keep parent selector as-is)
      await loadWaves()
    } catch (e) {
      setError(e?.responseText || e?.message || strings('dist_failed_create_wave'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <Box>
      <Heading
        as="h3"
        sx={{
          fontSize: [3, null, 4],
          lineHeight: 'body',
          mb: [3, null, 4]
        }}>
        {strings('dist_invite_tree_heading')}
      </Heading>

      {!enabled ? (
        <Text>{strings('dist_invite_tree_not_enabled')}</Text>
      ) : (
        <>
          <Box sx={{ mb: [3] }}>
            <Text sx={{ display: 'block', mb: [2] }}>
              {strings('dist_invite_only_desc')}
            </Text>
          </Box>

          <Heading
            as="h6"
            sx={{
              fontSize: [1, null, 2],
              lineHeight: 'body',
              my: [3, null, 3]
            }}>
            {strings('dist_waves')}
          </Heading>
          {loading ? (
            <Spinner />
          ) : error ? (
            <Text sx={{ color: 'error' }}>{String(error)}</Text>
          ) : hasWaves ? (
            <Box as="ul" sx={{ pl: [3], mb: [3] }}>
              {waves.map((w) => (
                <li key={w.id}>
                  <Text>
                    {`${strings('dist_wave')} ${w.wave} (${strings('dist_parent')} ${
                      w.parent_wave || 0
                    }) — ipu ${w.invites_per_user || 0}, ${strings('dist_owner')} ${
                      w.owner_invites || 0
                    }, ${strings('dist_size')} ${w.size || 0}`}
                  </Text>
                </li>
              ))}
            </Box>
          ) : (
            <Text>{strings('dist_no_waves_yet')}</Text>
          )}

          <Heading
            as="h6"
            sx={{
              fontSize: [1, null, 2],
              lineHeight: 'body',
              my: [3, null, 3]
            }}>
            {strings('dist_create_next_wave')}
          </Heading>

          <Box sx={{ mb: [3] }}>
            <Text sx={{ display: 'block', mb: [2] }}>
              {strings('dist_invites_per_user_desc')}
            </Text>
            <input
              type="number"
              min={0}
              max={1000}
              value={invitesPerUser}
              onChange={(e) => setInvitesPerUser(e.target.value)}
            />
          </Box>

          <Box sx={{ mb: [3] }}>
            <Text sx={{ display: 'block', mb: [2] }}>
              {strings('dist_owner_invites_desc')}
            </Text>
            <input
              type="number"
              min={0}
              max={1000}
              value={ownerInvites}
              onChange={(e) => setOwnerInvites(e.target.value)}
            />
          </Box>

          <Box sx={{ mb: [3] }}>
            <Text sx={{ display: 'block', mb: [2] }}>
              {strings('dist_parent_wave_desc')}
            </Text>
            <select
              value={parentWaveOverride}
              onChange={(e) => setParentWaveOverride(e.target.value)}>
              <option value="">{strings('dist_latest_default')}</option>
              <option value="0">{strings('dist_root_0')}</option>
              {waves.map((w) => (
                <option key={w.id} value={w.wave}>{`${strings('dist_wave')} ${w.wave}`}</option>
              ))}
            </select>
          </Box>

          <Box sx={{ mb: [3], mt: [3] }}>
            <Button sx={{ mt: [2] }} onClick={handleCreateWave} disabled={!canCreate || creating}>
              {creating ? strings('dist_creating') : strings('dist_create_wave')}
            </Button>
          </Box>

          {createdSummary ? (
            <Text>
              {`${strings('dist_created_wave')} ${createdSummary.wave}. ${strings(
                'dist_invites_created'
              )}: ${createdSummary.invites_created}`}
            </Text>
          ) : null}
        </>
      )}
    </Box>
  )
}

export default InviteTree
